import { apiFetch, readJsonSafe } from "./apiClient";

const requestJson = async (path, options = {}) => {
  const response = await apiFetch(path, options);
  const data = await readJsonSafe(response);

  if (!response.ok) {
    const error = new Error(data?.message || `Request failed with status ${response.status}.`);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
};

const requestBlob = async (path, options = {}) => {
  const response = await apiFetch(path, options);

  if (!response.ok) {
    const data = await readJsonSafe(response);
    const error = new Error(data?.message || `Request failed with status ${response.status}.`);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return response.blob();
};

export const syllabusApi = {
  latest() {
    return requestJson("/api/syllabus/latest");
  },

  getBySlug(slugWithId) {
    return requestJson(`/api/syllabus/${encodeURIComponent(slugWithId)}`);
  },

  getByExamKey(examKey) {
    return requestJson(`/api/syllabus/exam/${encodeURIComponent(examKey)}`);
  },

  getTestSeriesBlueprint(examKey) {
    return requestJson(
      `/api/syllabus/exam/${encodeURIComponent(examKey)}/test-series-blueprint`
    );
  },

  adminList() {
    return requestJson("/api/syllabus/admin", { auth: true });
  },

  adminGetById(id) {
    return requestJson(`/api/syllabus/admin/${encodeURIComponent(id)}`, { auth: true });
  },

  adminCreate(payload) {
    return requestJson("/api/syllabus/admin", {
      method: "POST",
      auth: true,
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
  },

  adminUpdate(id, payload) {
    return requestJson(`/api/syllabus/admin/${encodeURIComponent(id)}`, {
      method: "PUT",
      auth: true,
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
  },

  adminDelete(id) {
    return requestJson(`/api/syllabus/admin/${encodeURIComponent(id)}`, {
      method: "DELETE",
      auth: true,
    });
  },

  adminTestSeriesOverview() {
    return requestJson("/api/syllabus/admin/test-series/overview", { auth: true });
  },

  adminTestSeriesOverviewByExamKey(examKey) {
    return requestJson(
      `/api/syllabus/admin/test-series/overview/${encodeURIComponent(examKey)}`,
      { auth: true }
    );
  },

  adminGenerateTestSeries(examKey) {
    return requestJson(
      `/api/syllabus/admin/test-series/generate/${encodeURIComponent(examKey)}`,
      {
        method: "POST",
        auth: true,
      }
    );
  },

  adminQuestionBankCoverage(examKey) {
    return requestJson(
      `/api/syllabus/admin/question-bank/coverage/${encodeURIComponent(examKey)}`,
      { auth: true }
    );
  },

  adminGenerateQuestionBankPreview(examKey, payload) {
    return requestJson(
      `/api/syllabus/admin/question-bank/generate-preview/${encodeURIComponent(examKey)}`,
      {
        method: "POST",
        auth: true,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );
  },

  adminSaveQuestionBankPreview(payload) {
    return requestJson("/questions/bulk", {
      method: "POST",
      auth: true,
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
  },

  adminGenerateAndSaveQuestionBank(examKey, payload) {
    return requestJson(
      `/api/syllabus/admin/question-bank/generate-and-save/${encodeURIComponent(examKey)}`,
      {
        method: "POST",
        auth: true,
        timeoutMs: 15 * 60 * 1000,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );
  },

  adminDownloadQuestionBankArtifact(artifactId) {
    return requestBlob(
      `/api/syllabus/admin/question-bank/artifacts/${encodeURIComponent(artifactId)}`,
      {
        auth: true,
        timeoutMs: 2 * 60 * 1000,
      }
    );
  },
};
